'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { AlertTriangle, ImageOff, Loader2, Search, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { ArtworkCard } from '../../components/shop/ArtworkCard';
import { BrowseByNav } from '../../components/shop/BrowseByNav';
import { ShopToolbar } from '../../components/shop/ShopToolbar';
import { FilterPanel } from '../../components/shop/FilterPanel';
import { FilterSheet } from '../../components/shop/FilterSheet';
import { ActiveFilterChips } from '../../components/shop/ActiveFilterChips';
import { ShopPromoCard } from '../../components/shop/ShopPromoCard';
import {
  type SortOption,
  type FilterCategories,
  type ActiveFilters,
  type FilterKey,
  FILTER_KEYS,
  getActiveFilterCount,
} from '../../components/shop/filter-types';
import { useDebounce } from '../../hooks/useDebounce';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { PAGINATION_DEFAULTS } from '../../constants/layout';
import type { MarketplaceArtwork } from '@/types/marketplace';
import { PRODUCT_EVENTS, trackProductEvent } from '@/lib/analytics/product-events';

interface ArtworksResponse {
  artworks: MarketplaceArtwork[];
  pagination: {
    page: number;
    limit: number;
    hasMore: boolean;
    total?: number;
  };
}

interface ShopClientProps {
  initialArtworks: MarketplaceArtwork[];
  initialHasMore: boolean;
  initialTotal: number;
  initialSort: SortOption;
  initialSearch: string;
  categories: FilterCategories;
  initialFilters?: ActiveFilters;
}

const ShopClient = ({
  initialArtworks,
  initialHasMore,
  initialTotal,
  initialSort,
  initialSearch,
  categories,
  initialFilters = {},
}: ShopClientProps): React.ReactElement => {
  const t = useTranslations('shop.shopClient');
  const [artworks, setArtworks] = useState<MarketplaceArtwork[]>(initialArtworks);
  const [total, setTotal] = useState(initialTotal);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const { error, handleError, clearError } = useErrorHandler();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [showFilters, setShowFilters] = useState(false);
  const [forceOpenFilterKey, setForceOpenFilterKey] = useState<FilterKey | null>(null);
  const forceOpenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const artworksRequestRef = useRef<AbortController | null>(null);
  const subjectsGroupRef = useRef<HTMLDivElement | null>(null);
  const stylesGroupRef = useRef<HTMLDivElement | null>(null);
  const roomsGroupRef = useRef<HTMLDivElement | null>(null);
  const colorsGroupRef = useRef<HTMLDivElement | null>(null);
  const filterGroupRefs = {
    subjects: subjectsGroupRef,
    styles: stylesGroupRef,
    rooms: roomsGroupRef,
    colors: colorsGroupRef,
  };

  useEffect(() => {
    return () => {
      if (forceOpenTimeoutRef.current) clearTimeout(forceOpenTimeoutRef.current);
      artworksRequestRef.current?.abort();
    };
  }, []);

  // Filter state
  const [searchInput, setSearchInput] = useState(initialSearch);
  const debouncedSearch = useDebounce(searchInput, 300);
  const [sortBy, setSortBy] = useState<SortOption>(initialSort);
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>(initialFilters);
  const isInitialMountRef = useRef(true);
  const hasTrackedShopViewRef = useRef(false);
  const previousDebouncedSearchRef = useRef<string | null>(null);

  useEffect(() => {
    if (hasTrackedShopViewRef.current) {
      return;
    }

    hasTrackedShopViewRef.current = true;

    trackProductEvent(PRODUCT_EVENTS.SHOP_VIEWED, {
      surface: 'shop',
      entry_point: 'shop_page',
      artwork_count: initialArtworks.length,
      sort_by: initialSort,
      active_filters_count: getActiveFilterCount(initialFilters),
    });
  }, [initialArtworks.length, initialFilters, initialSort]);

  useEffect(() => {
    if (previousDebouncedSearchRef.current === null) {
      previousDebouncedSearchRef.current = debouncedSearch;
      return;
    }

    if (previousDebouncedSearchRef.current === debouncedSearch) {
      return;
    }

    const previousSearch = previousDebouncedSearchRef.current;
    previousDebouncedSearchRef.current = debouncedSearch;

    const url = new URL(window.location.href);
    if (debouncedSearch.trim()) {
      url.searchParams.set('search', debouncedSearch.trim());
    } else {
      url.searchParams.delete('search');
    }
    window.history.replaceState(null, '', url.toString());

    trackProductEvent(PRODUCT_EVENTS.SHOP_FILTER_APPLIED, {
      surface: 'shop',
      entry_point: 'search_input',
      filter_type: 'search',
      sort_by: sortBy,
      active_filters_count: getActiveFilterCount(activeFilters),
      previous_search_query_length: previousSearch.trim().length,
      search_query_length: debouncedSearch.trim().length,
    });
  }, [activeFilters, debouncedSearch, sortBy]);

  const fetchArtworks = useCallback(
    async (pageNum: number, append: boolean = false) => {
      artworksRequestRef.current?.abort();
      const abortController = new AbortController();
      artworksRequestRef.current = abortController;

      try {
        if (pageNum === 1) {
          setLoading(true);
        } else {
          setLoadingMore(true);
        }

        const params = new URLSearchParams({
          page: pageNum.toString(),
          limit: PAGINATION_DEFAULTS.SHOP_LIMIT.toString(),
          sort: sortBy,
        });

        // Only the first page needs the matching result count. Re-running the
        // COUNT query for every infinite-scroll page adds cost without changing
        // the toolbar total.
        if (pageNum === 1) {
          params.set('total', '1');
        }

        if (debouncedSearch) {
          params.set('search', debouncedSearch);
        }

        // Append active category filters as comma-joined handles
        for (const [cat, handles] of Object.entries(activeFilters)) {
          if (handles && handles.length > 0) {
            params.set(cat, handles.join(','));
          }
        }

        const response = await fetch(`/api/marketplace/artworks?${params}`, {
          signal: abortController.signal,
        });
        if (!response.ok) {
          throw new Error(t('errors.fetchArtworks'));
        }

        const data: ArtworksResponse = await response.json();
        if (abortController.signal.aborted) return;

        if (append) {
          setArtworks(prev => {
            const existingIds = new Set(prev.map(artwork => artwork.id));
            return [...prev, ...data.artworks.filter(artwork => !existingIds.has(artwork.id))];
          });
        } else {
          setArtworks(data.artworks);
        }

        if (typeof data.pagination.total === 'number') {
          setTotal(data.pagination.total);
        }
        setHasMore(data.pagination.hasMore);
        clearError();
      } catch (err) {
        if (!(err instanceof DOMException && err.name === 'AbortError')) {
          handleError(err, t('errors.fetchArtworks'));
        }
      } finally {
        if (artworksRequestRef.current === abortController) {
          artworksRequestRef.current = null;
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [sortBy, debouncedSearch, activeFilters, clearError, handleError, t],
  );

  // Fetch when sort or search changes (skip initial mount — SSR data is already correct)
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    setPage(1);
    fetchArtworks(1, false);
  }, [debouncedSearch, sortBy, activeFilters, fetchArtworks]);

  // Sync sort to URL without triggering a server re-render
  const handleSortChange = useCallback(
    (sort: SortOption) => {
      if (sort === sortBy) {
        return;
      }

      trackProductEvent(PRODUCT_EVENTS.SHOP_FILTER_APPLIED, {
        surface: 'shop',
        entry_point: 'sort_control',
        filter_type: 'sort',
        previous_sort_by: sortBy,
        sort_by: sort,
        active_filters_count: getActiveFilterCount(activeFilters),
        search_query_length: debouncedSearch.trim().length,
      });

      setSortBy(sort);

      const url = new URL(window.location.href);
      url.searchParams.set('sort', sort);
      window.history.replaceState(null, '', url.toString());
    },
    [activeFilters, debouncedSearch, sortBy],
  );

  // Handle search input change
  const handleSearchChange = useCallback((query: string) => {
    setSearchInput(query);
  }, []);

  // Infinite scroll
  const handleLoadMore = useCallback(() => {
    if (!hasMore || loadingMore || loading || artworksRequestRef.current) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchArtworks(nextPage, true);
  }, [hasMore, loadingMore, loading, page, fetchArtworks]);

  const sentinelRef = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    hasMore,
    isLoading: loading || loadingMore,
    threshold: PAGINATION_DEFAULTS.SCROLL_ROOT_MARGIN,
  });

  const applyFilter = useCallback(
    (filterType: FilterKey, values: string[]) => {
      const currentValues = activeFilters[filterType] ?? [];
      const hasChanged =
        currentValues.length !== values.length ||
        currentValues.some((value, index) => value !== values[index]);

      if (hasChanged) {
        const nextFilters = { ...activeFilters, [filterType]: values };

        trackProductEvent(PRODUCT_EVENTS.SHOP_FILTER_APPLIED, {
          surface: 'shop',
          entry_point: 'filter_menu',
          filter_type: filterType,
          selected_values_count: values.length,
          active_filters_count: getActiveFilterCount(nextFilters),
          sort_by: sortBy,
          search_query_length: debouncedSearch.trim().length,
        });
      }

      setActiveFilters(prev => ({ ...prev, [filterType]: values }));

      // Sync to URL without server re-render (must live outside the updater —
      // replaceState is a side-effect and updaters can run during render).
      const url = new URL(window.location.href);
      if (values.length > 0) {
        url.searchParams.set(filterType, values.join(','));
      } else {
        url.searchParams.delete(filterType);
      }
      window.history.replaceState(null, '', url.toString());
    },
    [activeFilters, debouncedSearch, sortBy],
  );

  // Toggle a single taxonomy value (used by browse-by cards, panel pills, chips)
  const toggleFilter = useCallback(
    (filterType: FilterKey, handle: string) => {
      const current = activeFilters[filterType] ?? [];
      const updated = current.includes(handle)
        ? current.filter(value => value !== handle)
        : [...current, handle];
      applyFilter(filterType, updated);
    },
    [activeFilters, applyFilter],
  );

  // Clear only the taxonomy filters — search stays intact.
  const handleClearFilters = useCallback(() => {
    const activeFiltersCount = getActiveFilterCount(activeFilters);
    if (activeFiltersCount === 0) return;

    trackProductEvent(PRODUCT_EVENTS.SHOP_FILTER_APPLIED, {
      surface: 'shop',
      entry_point: 'filter_menu',
      filter_type: 'clear_all',
      previous_active_filters_count: activeFiltersCount,
      previous_search_query_length: searchInput.trim().length,
      sort_by: sortBy,
    });

    setActiveFilters({});
    const url = new URL(window.location.href);
    for (const key of FILTER_KEYS) {
      url.searchParams.delete(key);
    }
    window.history.replaceState(null, '', url.toString());
  }, [activeFilters, searchInput, sortBy]);

  // Clear only the search query — active taxonomy filters stay intact.
  const handleClearSearch = useCallback(() => {
    setSearchInput('');
    const url = new URL(window.location.href);
    url.searchParams.delete('search');
    window.history.replaceState(null, '', url.toString());
  }, []);

  // "View all" from the browse-by row: on desktop, expand + scroll the
  // matching sidebar group into view; on mobile, open the filter sheet.
  const handleViewAll = useCallback((key: FilterKey) => {
    const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;

    if (!isDesktop) {
      setShowFilters(true);
      return;
    }

    setForceOpenFilterKey(key);
    filterGroupRefs[key].current?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    if (forceOpenTimeoutRef.current) clearTimeout(forceOpenTimeoutRef.current);
    forceOpenTimeoutRef.current = setTimeout(() => setForceOpenFilterKey(null), 1200);
    // filterGroupRefs holds stable ref objects for the component's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const activeFilterCount = getActiveFilterCount(activeFilters);

  return (
    <div className="min-h-screen pb-24">
      {/* Compact introduction + search + browse-by */}
      <section className="mx-auto max-w-7xl px-4 pt-3 sm:px-6 lg:px-8 lg:pt-5">
        <div className="lg:flex lg:items-start lg:justify-between lg:gap-8">
          <div className="min-w-0">
            <h1 className="font-display text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl lg:text-3xl">
              {t('intro.title')}
            </h1>
            <p className="mt-1 hidden max-w-xl text-sm text-gray-600 dark:text-gray-400 sm:block sm:text-base">
              {t('intro.subtitle')}
            </p>
          </div>

          {/* Search — inline with the heading on desktop, full width on mobile */}
          <div className="relative mt-3 lg:mt-0 lg:w-[440px] lg:shrink-0 xl:w-[480px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={searchInput}
              onChange={e => handleSearchChange(e.target.value)}
              placeholder={t('search.placeholder')}
              aria-label={t('search.placeholder')}
              className="h-12 w-full rounded-xl border border-gray-200 bg-white pl-10 pr-10 text-sm text-gray-900 placeholder-gray-500 transition-colors focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/40 dark:border-white/10 dark:bg-slate-900 dark:text-white dark:placeholder-gray-400 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-cancel-button]:appearance-none"
            />
            {searchInput && (
              <button
                onClick={() => handleSearchChange('')}
                aria-label={t('search.clear')}
                className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-gray-200"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Browse-by discovery */}
        <BrowseByNav
          categories={categories}
          activeFilters={activeFilters}
          onToggle={toggleFilter}
          onViewAll={handleViewAll}
        />
      </section>

      {/* Toolbar + filters + grid */}
      <section className="mx-auto mt-3 max-w-7xl px-4 sm:px-6 lg:px-8">
        <ShopToolbar
          total={total}
          loading={loading}
          sortBy={sortBy}
          onSortChange={handleSortChange}
          activeFilterCount={activeFilterCount}
          onOpenFilters={() => setShowFilters(true)}
        />

        <ActiveFilterChips
          categories={categories}
          activeFilters={activeFilters}
          onRemove={toggleFilter}
          onClearAll={handleClearFilters}
        />

        {/* align-items intentionally left at the default `stretch` (no
            items-start) — a sticky child needs its grid-item parent to be
            allowed to grow to the row's full height, or it has no room to
            travel before pinning and effectively never sticks. */}
        <div className="mt-4 lg:grid lg:grid-cols-[240px_1fr] lg:gap-8 xl:grid-cols-[260px_1fr]">
          {/* Desktop persistent filter sidebar — top-aligned with the grid */}
          <aside className="hidden lg:block">
            <div className="sticky top-[calc(var(--scroll-offset,64px)+4.5rem)] max-h-[calc(100vh-var(--scroll-offset,64px)-6rem)] overflow-y-auto pr-1 scrollbar-hide">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {t('filtersHeading')}
                </h2>
                {activeFilterCount > 0 && (
                  <button
                    onClick={handleClearFilters}
                    className="text-xs text-gray-500 underline transition-colors hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {t('clearAll')}
                  </button>
                )}
              </div>
              <FilterPanel
                categories={categories}
                activeFilters={activeFilters}
                onToggle={toggleFilter}
                groupRefs={filterGroupRefs}
                forceOpenKey={forceOpenFilterKey}
              />
            </div>
          </aside>

          {/* Artwork grid + states */}
          <div>
            {/* Loading skeleton */}
            {loading && (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 xl:grid-cols-4">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div
                    key={i}
                    className="aspect-square animate-pulse rounded-xl bg-gray-200 dark:bg-slate-800"
                  />
                ))}
              </div>
            )}

            {/* Full error state — only when there's nothing else to show */}
            {error && !loading && artworks.length === 0 && (
              <div className="py-10 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-red-50 dark:bg-red-500/10">
                  <AlertTriangle className="h-6 w-6 text-red-500" />
                </div>
                <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>
                <button
                  onClick={() => fetchArtworks(1, false)}
                  className="min-h-11 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:from-emerald-600 hover:to-teal-600"
                >
                  {t('actions.tryAgain')}
                </button>
              </div>
            )}

            {/* Inline error banner — a refetch failed but earlier results are still shown */}
            {error && !loading && artworks.length > 0 && (
              <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400">
                <span>{error}</span>
                <button
                  onClick={() => fetchArtworks(1, false)}
                  className="min-h-8 shrink-0 font-medium underline hover:no-underline"
                >
                  {t('actions.tryAgain')}
                </button>
              </div>
            )}

            {/* Empty state — compact, near the top of the grid area */}
            {!loading && !error && artworks.length === 0 && (
              <div className="py-10 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-slate-800">
                  <ImageOff className="h-6 w-6 text-gray-400" />
                </div>
                <h3 className="mb-1 text-base font-semibold text-gray-900 dark:text-white">
                  {t('empty.title')}
                </h3>
                <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                  {t('empty.description')}
                </p>
                {(activeFilterCount > 0 || searchInput.trim().length > 0) && (
                  <div className="flex items-center justify-center gap-4">
                    {activeFilterCount > 0 && (
                      <button
                        onClick={handleClearFilters}
                        className="min-h-11 px-2 text-sm text-amber-600 hover:underline dark:text-amber-400"
                      >
                        {t('actions.clearFilters')}
                      </button>
                    )}
                    {searchInput.trim().length > 0 && (
                      <button
                        onClick={handleClearSearch}
                        className="min-h-11 px-2 text-sm text-amber-600 hover:underline dark:text-amber-400"
                      >
                        {t('actions.clearSearch')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Grid */}
            {!loading && artworks.length > 0 && (
              <div
                id="art-grid"
                data-testid="shop-artwork-grid"
                className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 xl:grid-cols-4"
              >
                {artworks.map((artwork, index) => (
                  <ArtworkCard key={artwork.id} artwork={artwork} priority={index < 8} />
                ))}
              </div>
            )}

            {/* Load more sentinel */}
            <div ref={sentinelRef} className="flex h-20 items-center justify-center">
              {loadingMore && (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>{t('results.loadingMore')}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Create-your-art promotion, below the initial catalog results */}
        <div className="mt-10">
          <ShopPromoCard />
        </div>
      </section>

      {/* Mobile filter bottom sheet */}
      <FilterSheet
        open={showFilters}
        onClose={() => setShowFilters(false)}
        categories={categories}
        activeFilters={activeFilters}
        onToggle={toggleFilter}
        onClearAll={handleClearFilters}
        total={total}
      />
    </div>
  );
};

export default ShopClient;
